import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building2, Hammer, Gem, ClipboardList } from 'lucide-react';
import { Helmet } from 'react-helmet';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Ensure hero video plays
    const video = document.querySelector('video');
    if (video) {
      video.play().catch((error) => {
        console.log('Video autoplay prevented:', error);
      });
    }
  }, []);

  const services = [
    {
      icon: Building2,
      title: 'Interior Fit-Out',
      description: 'Complete interior construction and fit-out services for commercial spaces.',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc2MTYwMjM1M3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Hammer,
      title: 'Design-Build',
      description: 'Integrated design and construction services for streamlined project delivery.',
      image: 'https://customer-assets.emergentagent.com/job_build-marble/artifacts/mtpji2dh_WhatsApp%20Image%202025-11-08%20at%2021.01.29_8392183c.jpg',
    },
    {
      icon: ClipboardList,
      title: 'General Contracting',
      description: 'Comprehensive general contracting services for commercial construction projects.',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
    },
    {
      icon: ClipboardList,
      title: 'Project Management',
      description: 'End-to-end project management ensuring on-time, on-budget delivery.',
      image: 'https://customer-assets.emergentagent.com/job_build-marble/artifacts/5fm72sm9_Generated%20Image%20November%2010%2C%202025%20-%2011_59AM.png',
    },
  ];

  return (
    <main className="min-h-screen">
      <Helmet>
        <title>Commercial Construction Toronto & GTA | Interior Fit-Out Design-Build | Oracle Project Group</title>
        <meta name="description" content="Leading commercial construction company in Toronto & GTA. Expert interior fit-outs, design-build solutions, general contracting. Licensed & insured. 10+ years. Free quote: (647) 631-8914." />
        <link rel="canonical" href="https://oracleprojectgroup.com/" />
        <meta property="og:title" content="Commercial Construction Toronto & GTA | Oracle Project Group" />
        <meta property="og:description" content="Premier commercial construction, interior fit-out, and design-build services in Toronto & GTA." />
        <meta property="og:url" content="https://oracleprojectgroup.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Commercial Construction Toronto & GTA | Oracle Project Group" />
        <meta name="twitter:description" content="Premier commercial construction, interior fit-out, and design-build services in Toronto & GTA." />
        <meta name="keywords" content="commercial construction Toronto, interior fit-out Toronto, design-build contractor GTA, general contractor Toronto, construction company GTA, office renovation Toronto" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": "Oracle Project Group - Construction Excellence",
              "description": "Showcasing precision construction and commercial contracting services in Toronto & GTA",
              "thumbnailUrl": "https://customer-assets.emergentagent.com/job_build-marble/artifacts/fr2rv7oy_Color%20logo%20-%20no%20background.png",
              "uploadDate": "2025-01-01",
              "contentUrl": "https://customer-assets.emergentagent.com/job_buildoracle/artifacts/au4mcthj_202511120952%20%281%29.mp4",
              "embedUrl": "https://oracleprojectgroup.com"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" aria-label="Hero banner">
        {/* Desktop Video - Full Coverage, Cinematic */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="hero-video-desktop absolute inset-0 w-full h-full object-cover"
          style={{
            imageRendering: 'high-quality',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            filter: 'contrast(1.08) saturate(1.12) brightness(1.02)',
          }}
          aria-label="Construction site video showcasing Oracle Project Group's work"
          ref={(video) => {
            if (video) {
              video.play().catch((error) => console.log('Video autoplay prevented:', error));
            }
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_buildoracle/artifacts/au4mcthj_202511120952%20%281%29.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Mobile Video - Optimized for Mobile Screens */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="hero-video-mobile absolute inset-0 w-full h-full object-cover"
          style={{
            imageRendering: 'high-quality',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            filter: 'contrast(1.1) saturate(1.15) brightness(1.03)',
          }}
          aria-label="Construction site video for mobile showcasing Oracle Project Group's work"
          ref={(video) => {
            if (video) {
              video.play().catch((error) => console.log('Video autoplay prevented:', error));
            }
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_buildoracle/artifacts/9t4tctrx_oracle%20mobile%20hero.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Cinematic Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50 pointer-events-none"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 fade-up drop-shadow-2xl" style={{ animationDelay: '0.4s', textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
            Precision Construction Without Compromise.
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 fade-up drop-shadow-lg" style={{ animationDelay: '0.6s', textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}>
            Oracle Project Group delivers turnkey construction, fit-outs, and general contracting services across the GTA.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up" style={{ animationDelay: '0.8s' }}>
            <Link
              to="/contact"
              className="px-8 py-4 bg-[#D4AF37] text-white rounded-md hover:bg-[#C19A2B] transition-all btn-transition font-semibold text-lg inline-flex items-center justify-center shadow-2xl hover:shadow-[#D4AF37]/50 hover:scale-105"
            >
              Request a Consultation
              <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link
              to="/projects"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white text-white rounded-md hover:bg-white hover:text-[#0F1115] transition-all btn-transition font-semibold text-lg shadow-xl"
            >
              View Projects
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1115] mb-4">
              Construction Excellence
            </h2>
            <div className="w-24 h-1 bg-[#C6A45B] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
Oracle Project Group is the leader in the construction industry. We specialize in delivering high-quality construction projects with precision and expertise.              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
                  <p className="text-gray-700">Over 10 years of construction excellence</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
                  <p className="text-gray-700">Licensed and insured professionals</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-1" size={24} />
                  <p className="text-gray-700">Commitment to quality and safety</p>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center text-[#D4AF37] font-semibold hover:text-[#C19A2B] transition-colors"
              >
                Learn More About Us
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_build-marble/artifacts/2w882zt4_TJG_2_BloorstE_3WEB.jpg"
                alt="Oracle Project Group commercial construction project - modern office building interior"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#F6F6F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F1115] mb-4">
              Our Services
            </h2>
            <div className="w-24 h-1 bg-[#C6A45B] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Leveraging our expertise, we take a unique collaborative approach for a seamless project flow, from start to finish.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.title} - Professional construction services by Oracle Project Group in Toronto`}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#0F1115] mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link
                      to="/services"
                      className="inline-flex items-center text-[#D4AF37] font-semibold hover:text-[#C19A2B] transition-colors text-sm"
                    >
                      Learn More
                      <ArrowRight className="ml-2" size={16} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block px-8 py-3 bg-[#D4AF37] text-white rounded-md hover:bg-[#C19A2B] transition-all btn-transition font-semibold"
            >
              See All Our Services
            </Link>
          </div>
        </div>
      </section>

      {/* Projects CTA Section */}
      <section className="py-20 bg-[#0F1115] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Explore Our Portfolio
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Discover our completed projects showcasing precision, quality, and excellence in commercial construction.
          </p>
          <Link
            to="/projects"
            className="inline-block px-8 py-4 bg-[#C6A45B] text-white rounded-md hover:bg-[#B8860B] transition-all btn-transition font-semibold text-lg"
          >
            See All Our Projects
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;
